// StudentValidation.js - Validation rules for Student Create/Edit form

// ==================== HELPER FUNCTIONS ====================

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {Boolean} - True if email is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate mobile number (10 digits, starting with 6-9 for Indian numbers)
 * @param {string} mobile - Mobile number to validate
 * @returns {Boolean} - True if mobile is valid
 */
export const isValidMobile = (mobile) => {
  if (!mobile) return false;
  const mobileRegex = /^[6-9][0-9]{9}$/;
  return mobileRegex.test(mobile.trim());
};

/**
 * Validate Aadhar number (12 digits, not starting with 0 or 1)
 * @param {string} aadhar - Aadhar number to validate
 * @returns {Boolean} - True if Aadhar is valid
 */
export const isValidAadhar = (aadhar) => {
  if (!aadhar) return false;
  const aadharRegex = /^[2-9][0-9]{11}$/;
  return aadharRegex.test(aadhar.trim());
};

/**
 * Validate Pincode (6 digits, not starting with 0)
 * @param {string} pincode - Pincode to validate
 * @returns {Boolean} - True if pincode is valid
 */
export const isValidPincode = (pincode) => {
  if (!pincode) return false;
  const pincodeRegex = /^[1-9][0-9]{5}$/;
  return pincodeRegex.test(pincode.trim());
};

/**
 * Validate PAN number format (AAAAA9999A)
 * @param {string} pan - PAN number to validate
 * @returns {Boolean} - True if PAN is valid
 */
export const isValidPAN = (pan) => {
  if (!pan) return false;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan.toUpperCase().trim());
};

/**
 * Sanitize input - remove extra spaces
 * @param {string} value - Value to sanitize
 * @returns {string} - Sanitized value
 */
export const sanitizeInput = (value) => {
  if (typeof value !== "string") return value;
  return value.trim().replace(/\s+/g, " ");
};

/**
 * Format mobile number input (only digits, max 10)
 * @param {string} value - Input value
 * @returns {string} - Formatted value
 */
export const formatMobileInput = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, "").slice(0, 10);
};

/**
 * Format Aadhar number input (only digits, max 12)
 * @param {string} value - Input value
 * @returns {string} - Formatted value
 */
export const formatAadharInput = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, "").slice(0, 12);
};

/**
 * Format Pincode input (only digits, max 6)
 * @param {string} value - Input value
 * @returns {string} - Formatted value
 */
export const formatPincodeInput = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, "").slice(0, 6);
};

/**
 * Format PAN input (uppercase, alphanumeric)
 * @param {string} value - Input value
 * @returns {string} - Formatted value
 */
export const formatPANInput = (value) => {
  if (!value) return "";
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
};

// ==================== VALIDATION FUNCTIONS ====================

/**
 * Validate Student Information Section
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateStudentInfo = (formData) => {
  const errors = {};

  if (!formData.firstName?.trim()) {
    errors.firstName = "First name is required";
  } else if (formData.firstName.trim().length < 2) {
    errors.firstName = "First name must be at least 2 characters";
  }

  if (!formData.lastName?.trim()) {
    errors.lastName = "Last name is required";
  } 
  // else if (formData.lastName.trim().length < 2) {
  //   errors.lastName = "Last name must be at least 2 characters";
  // }

  if (!formData.emailAddress?.trim()) {
    errors.emailAddress = "Email is required";
  } else if (!isValidEmail(formData.emailAddress)) {
    errors.emailAddress = "Please enter a valid email address";
  }

  if (!formData.dateOfBirth) {
    errors.dateOfBirth = "Date of birth is required";
  }

  if (!formData.gender) {
    errors.gender = "Gender is required";
  }

  if (!formData.class) {
    errors.class = "Class is required";
  }

  if (!formData.section) {
    errors.section = "Section is required";
  }

  if (!formData.religion) {
    errors.religion = "Religion is required";
  }

  if (!formData.category) {
    errors.category = "Category is required";
  }

  if (formData.aadharNumber?.trim()) {
    if (formData.aadharNumber.length !== 12) {
      errors.aadharNumber = "Aadhar number must be 12 digits";
    } else if (!isValidAadhar(formData.aadharNumber)) {
      errors.aadharNumber = "Please enter a valid Aadhar number";
    }
  }

  return errors;
};

/**
 * Validate Father Information Section
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateFatherInfo = (formData) => {
  const errors = {};

  if (!formData.fatherFirstName?.trim()) {
    errors.fatherFirstName = "First name is required";
  }

  if (!formData.fatherLastName?.trim()) {
    errors.fatherLastName = "Last name is required";
  }

  if (!formData.fatherEmail?.trim()) {
    errors.fatherEmail = "Email is required";
  } else if (!isValidEmail(formData.fatherEmail)) {
    errors.fatherEmail = "Please enter a valid email address";
  }

  if (!formData.fatherMobile?.trim()) {
    errors.fatherMobile = "Mobile number is required";
  } else if (formData.fatherMobile.length !== 10) {
    errors.fatherMobile = "Mobile number must be 10 digits";
  } else if (!isValidMobile(formData.fatherMobile)) {
    errors.fatherMobile = "Please enter a valid mobile number";
  }

  if (!formData.fatherAddress1?.trim()) {
    errors.fatherAddress1 = "Address is required";
  }

  if (!formData.fatherPincode?.trim()) {
    errors.fatherPincode = "Pincode is required";
  } else if (formData.fatherPincode.length !== 6) {
    errors.fatherPincode = "Pincode must be 6 digits";
  } else if (!isValidPincode(formData.fatherPincode)) {
    errors.fatherPincode = "Please enter a valid pincode";
  }

  if (!formData.fatherCity?.trim()) {
    errors.fatherCity = "City is required";
  }

  if (!formData.fatherState) {
    errors.fatherState = "State is required";
  }

  if (!formData.fatherCountry) {
    errors.fatherCountry = "Country is required";
  }

  if (!formData.fatherIncome) {
    errors.fatherIncome = "Income is required";
  }

  if (formData.fatherAadhar?.trim()) {
    if (formData.fatherAadhar.length !== 12) {
      errors.fatherAadhar = "Aadhar number must be 12 digits";
    } else if (!isValidAadhar(formData.fatherAadhar)) {
      errors.fatherAadhar = "Please enter a valid Aadhar number";
    }
  }

  return errors;
};

/**
 * Validate Mother Information Section
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateMotherInfo = (formData) => {
  const errors = {};

  if (!formData.motherFirstName?.trim()) {
    errors.motherFirstName = "First name is required";
  }

  if (!formData.motherLastName?.trim()) {
    errors.motherLastName = "Last name is required";
  }

  if (!formData.motherEmail?.trim()) {
    errors.motherEmail = "Email is required";
  } else if (!isValidEmail(formData.motherEmail)) {
    errors.motherEmail = "Please enter a valid email address";
  }

  if (!formData.motherMobile?.trim()) {
    errors.motherMobile = "Mobile number is required";
  } else if (formData.motherMobile.length !== 10) {
    errors.motherMobile = "Mobile number must be 10 digits";
  } else if (!isValidMobile(formData.motherMobile)) {
    errors.motherMobile = "Please enter a valid mobile number";
  }

  // Only validate address fields if "Same Address" is not checked
  if (!formData.motherSameAddress) {
    if (!formData.motherAddress1?.trim()) {
      errors.motherAddress1 = "Address is required";
    }

    if (!formData.motherPincode?.trim()) {
      errors.motherPincode = "Pincode is required";
    } else if (formData.motherPincode.length !== 6) {
      errors.motherPincode = "Pincode must be 6 digits";
    } else if (!isValidPincode(formData.motherPincode)) {
      errors.motherPincode = "Please enter a valid pincode";
    }

    if (!formData.motherCity?.trim()) {
      errors.motherCity = "City is required";
    }

    if (!formData.motherState) {
      errors.motherState = "State is required";
    }

    if (!formData.motherCountry) {
      errors.motherCountry = "Country is required";
    }
  }

  if (!formData.motherIncome) {
    errors.motherIncome = "Income is required";
  }

  if (formData.motherAadhar?.trim()) {
    if (formData.motherAadhar.length !== 12) {
      errors.motherAadhar = "Aadhar number must be 12 digits";
    } else if (!isValidAadhar(formData.motherAadhar)) {
      errors.motherAadhar = "Please enter a valid Aadhar number";
    }
  }

  return errors;
};

/**
 * Validate Guardian Selection
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateGuardianSelection = (formData) => {
  const errors = {};

  if (!formData.isChildStayingWithGuardian) {
    errors.isChildStayingWithGuardian = "Please select an option";
  }

  return errors;
};

/**
 * Validate Guardian Information Section (only when child is staying with guardian)
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateGuardianInfo = (formData) => {
  const errors = {};

  // Only validate if child is staying with guardian
  if (formData.isChildStayingWithGuardian !== "yes") {
    return errors;
  }

  if (!formData.guardianFirstName?.trim()) {
    errors.guardianFirstName = "First name is required";
  }

  if (!formData.guardianLastName?.trim()) {
    errors.guardianLastName = "Last name is required";
  }

  if (!formData.guardianEmail?.trim()) {
    errors.guardianEmail = "Email is required";
  } else if (!isValidEmail(formData.guardianEmail)) {
    errors.guardianEmail = "Please enter a valid email address";
  }

  if (!formData.guardianMobile?.trim()) {
    errors.guardianMobile = "Mobile number is required";
  } else if (formData.guardianMobile.length !== 10) {
    errors.guardianMobile = "Mobile number must be 10 digits";
  } else if (!isValidMobile(formData.guardianMobile)) {
    errors.guardianMobile = "Please enter a valid mobile number";
  }

  if (!formData.guardianAddress1?.trim()) {
    errors.guardianAddress1 = "Address is required";
  }

  if (!formData.guardianPincode?.trim()) {
    errors.guardianPincode = "Pincode is required";
  } else if (formData.guardianPincode.length !== 6) {
    errors.guardianPincode = "Pincode must be 6 digits";
  } else if (!isValidPincode(formData.guardianPincode)) {
    errors.guardianPincode = "Please enter a valid pincode";
  }

  if (!formData.guardianCity?.trim()) {
    errors.guardianCity = "City is required";
  }

  if (!formData.guardianState) {
    errors.guardianState = "State is required";
  }

  if (!formData.guardianCountry) {
    errors.guardianCountry = "Country is required";
  }

  if (!formData.guardianIncome) {
    errors.guardianIncome = "Income is required";
  }

  if (formData.guardianAadhar?.trim()) {
    if (formData.guardianAadhar.length !== 12) {
      errors.guardianAadhar = "Aadhar number must be 12 digits";
    } else if (!isValidAadhar(formData.guardianAadhar)) {
      errors.guardianAadhar = "Please enter a valid Aadhar number";
    }
  }

  return errors;
};

/**
 * Validate Emergency Contact 1 Section
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateEmergencyContact1 = (formData) => {
  const errors = {};

  if (!formData.emergency1FirstName?.trim()) {
    errors.emergency1FirstName = "First name is required";
  }

  if (!formData.emergency1LastName?.trim()) {
    errors.emergency1LastName = "Last name is required";
  }

  if (!formData.emergency1Email?.trim()) {
    errors.emergency1Email = "Email is required";
  } else if (!isValidEmail(formData.emergency1Email)) {
    errors.emergency1Email = "Please enter a valid email address";
  }

  if (!formData.emergency1Phone?.trim()) {
    errors.emergency1Phone = "Phone number is required";
  } else if (formData.emergency1Phone.length !== 10) {
    errors.emergency1Phone = "Phone number must be 10 digits";
  } else if (!isValidMobile(formData.emergency1Phone)) {
    errors.emergency1Phone = "Please enter a valid phone number";
  }

  if (!formData.emergency1RelationType) {
    errors.emergency1RelationType = "Relation type is required";
  }

  return errors;
};

/**
 * Validate Emergency Contact 2 Section (Optional - only validate if any field is filled)
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateEmergencyContact2 = (formData) => {
  const errors = {};

  // Check if any field in Emergency Contact 2 is filled
  const hasAnyValue =
    formData.emergency2FirstName?.trim() ||
    formData.emergency2MiddleName?.trim() ||
    formData.emergency2LastName?.trim() ||
    formData.emergency2Email?.trim() ||
    formData.emergency2Phone?.trim() ||
    formData.emergency2RelationType;

  // If no values, skip validation (optional section)
  if (!hasAnyValue) {
    return errors;
  }

  // If any value is present, validate format if filled
  if (formData.emergency2Email?.trim() && !isValidEmail(formData.emergency2Email)) {
    errors.emergency2Email = "Please enter a valid email address";
  }

  if (formData.emergency2Phone?.trim()) {
    if (formData.emergency2Phone.length !== 10) {
      errors.emergency2Phone = "Phone number must be 10 digits";
    } else if (!isValidMobile(formData.emergency2Phone)) {
      errors.emergency2Phone = "Please enter a valid phone number";
    }
  }

  return errors;
};

/**
 * Validate Family Doctor Section (Optional - only validate if any field is filled)
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors object
 */
export const validateFamilyDoctor = (formData) => {
  const errors = {};

  // Check if any field in Family Doctor is filled
  const hasAnyValue =
    formData.doctorFirstName?.trim() ||
    formData.doctorMiddleName?.trim() ||
    formData.doctorLastName?.trim() ||
    formData.doctorEmail?.trim() ||
    formData.doctorPhone?.trim() ||
    formData.doctorHospital?.trim();

  // If no values, skip validation (optional section)
  if (!hasAnyValue) {
    return errors;
  }

  // If any value is present, validate format if filled
  if (formData.doctorEmail?.trim() && !isValidEmail(formData.doctorEmail)) {
    errors.doctorEmail = "Please enter a valid email address";
  }

  if (formData.doctorPhone?.trim()) {
    if (formData.doctorPhone.length !== 10) {
      errors.doctorPhone = "Phone number must be 10 digits";
    } else if (!isValidMobile(formData.doctorPhone)) {
      errors.doctorPhone = "Please enter a valid phone number";
    }
  }

  return errors;
};

/**
 * Validate entire form
 * @param {Object} formData - Form data object
 * @returns {Object} - Combined errors object from all sections
 */
export const validateStudentForm = (formData) => {
  const studentErrors = validateStudentInfo(formData);
  const fatherErrors = validateFatherInfo(formData);
  const motherErrors = validateMotherInfo(formData);
  const guardianSelectionErrors = validateGuardianSelection(formData);
  const guardianErrors = validateGuardianInfo(formData);
  const emergency1Errors = validateEmergencyContact1(formData);
  const emergency2Errors = validateEmergencyContact2(formData);
  const doctorErrors = validateFamilyDoctor(formData);

  return {
    ...studentErrors,
    ...fatherErrors,
    ...motherErrors,
    ...guardianSelectionErrors,
    ...guardianErrors,
    ...emergency1Errors,
    ...emergency2Errors,
    ...doctorErrors,
  };
};

/**
 * Check if form is valid
 * @param {Object} formData - Form data object
 * @returns {Boolean} - True if form is valid
 */
export const isFormValid = (formData) => {
  const errors = validateStudentForm(formData);
  return Object.keys(errors).length === 0;
};

/**
 * Get validation errors for a specific section
 * @param {string} section - Section name
 * @param {Object} formData - Form data object
 * @returns {Object} - Errors for the specific section
 */
export const getValidationErrorsBySection = (section, formData) => {
  switch (section) {
    case "student":
      return validateStudentInfo(formData);
    case "father":
      return validateFatherInfo(formData);
    case "mother":
      return validateMotherInfo(formData);
    case "guardianSelection":
      return validateGuardianSelection(formData);
    case "guardian":
      return validateGuardianInfo(formData);
    case "emergency1":
      return validateEmergencyContact1(formData);
    case "emergency2":
      return validateEmergencyContact2(formData);
    case "doctor":
      return validateFamilyDoctor(formData);
    default:
      return {};
  }
};

export default {
  validateStudentForm,
  validateStudentInfo,
  validateFatherInfo,
  validateMotherInfo,
  validateGuardianSelection,
  validateGuardianInfo,
  validateEmergencyContact1,
  validateEmergencyContact2,
  validateFamilyDoctor,
  isFormValid,
  getValidationErrorsBySection,
  isValidEmail,
  isValidMobile,
  isValidAadhar,
  isValidPincode,
  isValidPAN,
  sanitizeInput,
  formatMobileInput,
  formatAadharInput,
  formatPincodeInput,
  formatPANInput,
};